import { Component, OnInit, Input } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { Profile, Certification, CertificationAttachment, Location } from '../../models/profile.model';
import { ProfileService } from '../../services/profile.service';
import { TabComponent } from '../tab-component';
import { TslDialog, TslSelectChange, PageEvent, TslSort } from '@tesla/ng-tds';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { StateService } from 'app/profile/services/state.service';
import { I18nService } from 'app/shared/services/i18n.service';
import { CommonService } from 'app/profile/services/common.service';
import * as FileSaver from 'file-saver';
import { Status, StatusEnum } from 'app/profile/models/state.model';
import { MatDialog } from '@angular/material/dialog';
import { CertificationDialogComponent } from './certification-dialog/certification-dialog.component';
import { Store, select } from '@ngrx/store';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import { SelectSupplierProfile, getUnitOfMeasure, getGeneralSettings, SelectSupplierLocations } from 'app/profile/store/app.selector';
import { GetUnitOfMeasure, GetCommodities, GetSupplierLocationsByCode, GetSupplierProfile } from 'app/profile/store/app.actions';
import { UnitOfMeasure, SiteInformation } from 'app/profile/models/site.model';
import { SiteService } from 'app/profile/services/site.service';
import { GeneralSettings } from 'app/profile/models/generic.model';

@Component({
    selector: 'app-site',
    templateUrl: './site.component.html',
    styleUrls: ['./site.component.scss']
})
export class SiteComponent implements OnInit, TabComponent {
    @Input() data: any;
    supplierCode: string;
    profile: Profile;
    deleteTitle: string;
    deleteConfirmation: string;
    siteForm: FormGroup;

    // locations
    currentLocationId = 0;
    locations$: Observable<Location[]>;
    unitOfMeasure: UnitOfMeasure[];
    currentSite: SiteInformation;

    // certifications table
    pageIndex = 0;
    pageSize = 10;
    pageSizeOptions: number[] = [10, 25, 50, 100, 250];
    datasource: BehaviorSubject<Array<Certification>> = new BehaviorSubject([]);
    certifications: Certification[] = [];
    certificationColumns = ['type', 'certified', 'expired', 'certifier', 'status', 'attachments', 'action'];
    helpUrl: string;
    settings: GeneralSettings;

    get measureType(): string {
        const index = this.unitOfMeasure.findIndex(u => u.id === this.siteForm.get('measureType').value);
        return index !== -1 ? this.unitOfMeasure[index].measureType : '';
    }
    get plantArea(): number { return this.siteForm.value.productionSize + this.siteForm.value.warehouseSize; }

    constructor(
        private route: ActivatedRoute,
        private fb: FormBuilder,
        private stateService: StateService,
        private service: ProfileService,
        private siteService: SiteService,
        public translate: I18nService,
        private dialog: TslDialog,
        private commonService: CommonService,
        private store: Store<SupplierState>,
        private matDialog: MatDialog) {
        this.createForms();
    }

    ngOnInit() {
        this.store.select(getGeneralSettings).subscribe(res => {
            this.settings = res;
            if (res) {
                this.helpUrl = this.settings.settings.siteHelpUrl;
            }
        });
        this.route.parent.params.subscribe(params => {
            if (!params["code"]) {
                return;
            }

            this.supplierCode = this.commonService.getSupplierCode(params["code"]);
            if (this.supplierCode !== null) {
                this.loadResources();
            }
        });
    }

    isDirty() {
        return false;
    }

    onSaveTab(): void {
        if (this.siteForm.invalid) {
            return;
        }

        this.currentSite.measureTypeId = this.siteForm.value.measureType;
        this.currentSite.productionSize = this.siteForm.value.productionSize;
        this.currentSite.warehouseSize = this.siteForm.value.warehouseSize;
        this.currentSite.officeSize = this.siteForm.value.officeSize;
        this.currentSite.manufacturingEmployeeNo = this.siteForm.value.manufacturingEmployeeNo;
        this.currentSite.engineerEmployeeNo = this.siteForm.value.engineerEmployeeNo;

        if (this.currentSite.id > 0) {
            this.siteService.putSiteInformation(this.supplierCode, this.currentLocationId, this.currentSite.id, this.currentSite)
                .subscribe(res => {
                    this.loadSiteInformation(res);
                });
        } else {
            this.siteService.postSiteInformation(this.supplierCode, this.currentLocationId, this.currentSite)
                .subscribe(res => {
                    this.loadSiteInformation(res);
                });
        }
    }

    onChangeLocation(event: TslSelectChange): void {
        this.currentLocationId = parseInt(event.value, 0);
        if (this.currentLocationId > 0) {
            this.siteService.getSiteInformation(this.supplierCode, this.currentLocationId)
                .subscribe((site: SiteInformation) => {
                    this.loadSiteInformation(site);
                });

            this.loadCertifications();
        }
    }

    onRemoveCertification(cert: Certification): void {
        this.dialog.confirm(this.translate.get(this.deleteTitle), this.translate.get(this.deleteConfirmation))
            .afterClosed()
            .subscribe(confirmed => {
                if (confirmed) {
                    this.service.deleteCertification(this.supplierCode, cert.id)
                        .subscribe(_ => {
                            this.stateService.currentStatus.next(new Status(StatusEnum.Success));
                            const index = this.certifications.indexOf(cert);
                            if (index > -1) {
                                this.certifications.splice(index, 1);
                                this.loadCertificationsPage();
                            }
                        });
                }
            });
    }

    onDeleteCertificationAttachment(attach: CertificationAttachment, item: Certification): void {
        this.dialog.confirm(this.translate.get(this.deleteTitle), this.translate.get(this.deleteConfirmation))
            .afterClosed()
            .subscribe(confirmed => {
                if (confirmed) {
                    this.service.deleteCertificationAttachment(this.supplierCode, attach.id)
                        .subscribe(_ => {
                            const index = item.attachments.indexOf(attach);
                            if (index > -1) {
                                item.attachments.splice(index, 1);
                            }
                            this.stateService.currentStatus.next(new Status(StatusEnum.Success));
                        });
                }
            });
    }

    onDownloadCertificatonAttachment(attach: CertificationAttachment): void {
        this.service.downloadCertificationAttachment(this.supplierCode, attach.storageKey)
            .subscribe(file => {
                FileSaver.saveAs(file, attach.fileName);
            });
    }

    onAddCertification(): void {
        this.openCertificationDialog(new Certification());
    }

    onEditCertification(model: Certification): void {
        this.openCertificationDialog(model);
    }

    onSortChange(sort: TslSort): void {
        const orderedItems = this.certifications.sort((a, b) => {
            const isAsc = sort.direction === 'asc';

            switch (sort.active) {
                case 'type': return compare(a.certificationType.toLowerCase(), b.certificationType.toLowerCase(), isAsc);
                case 'certified': return compare(a.certifiedDate.getMilliseconds(), b.certifiedDate.getMilliseconds(), isAsc);
                case 'expired': return compare(a.expirationDate.getMilliseconds(), b.expirationDate.getMilliseconds(), isAsc);
                case 'certifier': return compare(a.certifierName.toLowerCase(), b.certifierName.toLowerCase(), isAsc);
                default: return 0;
            }
        });

        const skip = this.pageIndex * this.pageSize;
        const take = (this.pageIndex + 1) * this.pageSize;
        this.datasource.next(orderedItems.slice(skip, take));

        function compare(a: number | string, b: number | string, isAsc: boolean) {
            return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
        }
    }

    onPageChange(pageChange: PageEvent): void {
        const { pageIndex, pageSize } = pageChange;
        this.pageIndex = pageIndex;
        this.pageSize = pageSize;
        this.loadCertificationsPage();
    }

    getStatus(expiration: Date): string {
        if (expiration) {
            const expirationDate = new Date(expiration);
            return Date.now() < expirationDate.getTime() ? this.translate.get('active') : this.translate.get('expired');
        }
        else {
            return this.translate.get("active");
        }
    }

    private loadSiteInformation(site: SiteInformation): void {
        this.currentSite = site || new SiteInformation();
        this.siteForm.patchValue({
            productionSize: this.currentSite.productionSize,
            warehouseSize: this.currentSite.warehouseSize,
            officeSize: this.currentSite.officeSize,
            manufacturingEmployeeNo: this.currentSite.manufacturingEmployeeNo,
            engineerEmployeeNo: this.currentSite.engineerEmployeeNo,
            measureType: this.currentSite.measureTypeId,
        });
    }

    private loadCertifications(): void {
        this.service.getCertifications(this.supplierCode, this.currentLocationId)
            .subscribe(res => {
                this.certifications = res;
                this.loadCertificationsPage();
            });
    }

    private loadCertificationsPage(): void {
        const skip = this.pageIndex * this.pageSize;
        const take = (this.pageIndex + 1) * this.pageSize;
        this.datasource.next(this.certifications.slice(skip, take));
    }

    private openCertificationDialog(cert: Certification): void {
        cert.supplierCode = this.supplierCode;
        cert.siteId = this.currentLocationId;
        const dialogRef = this.matDialog.open(CertificationDialogComponent, { width: '40%', height: '400px', data: cert });

        dialogRef.afterClosed()
            .subscribe((model: Certification) => {
                if (model === undefined) {
                    return;
                }

                if (model.id === undefined || model.id === 0) {
                    // create
                    this.service.postCertification(this.supplierCode, model)
                        .subscribe(_ => {
                            if (model.newFiles.length > 0) {
                                const fileLength = model.newFiles.length;
                                for (let i = 0; i < fileLength; i++) {
                                    this.uploadCertificationFile(_.id, model.newFiles[i], i === (fileLength - 1));
                                }
                            } else {
                                this.loadCertifications();
                            }

                            this.stateService.currentStatus.next(new Status(StatusEnum.Success));
                        });
                } else {
                    // update
                    this.service.putCertification(this.supplierCode, model)
                        .subscribe(_ => {
                            if (model.newFiles.length > 0) {
                                const fileLength = model.newFiles.length;
                                for (let i = 0; i < fileLength; i++) {
                                    this.uploadCertificationFile(_.id, model.newFiles[i], i === (fileLength - 1));
                                }
                            } else {
                                this.loadCertifications();
                            }

                            this.stateService.currentStatus.next(new Status(StatusEnum.Success));
                        });
                }
            });
    }

    private uploadCertificationFile(certificationId: number, file: File, forceReload = false): void {
        this.service.postCertificationAttachment(this.supplierCode, certificationId, file)
            .subscribe(_ => {
                this.stateService.currentStatus.next(new Status(StatusEnum.Success));
                if (forceReload) {
                    this.loadCertifications();
                }
            });
    }

    private loadResources(): void {

        this.store.dispatch(new GetUnitOfMeasure());

        this.store.pipe(select(SelectSupplierProfile))
            .subscribe(res => {
                if(!res){
                    this.store.dispatch(new GetSupplierProfile(this.supplierCode));
                }
                else {
                    this.profile = res;
                    this.siteForm.patchValue(res);
                    if((this.profile.supplierCode != this.supplierCode)){
                        this.store.dispatch(new GetSupplierLocationsByCode(this.supplierCode, true));
                    }
                    else {
                        this.store.dispatch(new GetSupplierLocationsByCode(this.supplierCode));
                    }
                    this.locations$ = this.store.pipe(select(SelectSupplierLocations));
                }

            });
        this.store.dispatch(new GetCommodities());
        this.store.pipe(select(getUnitOfMeasure)).subscribe(res => this.unitOfMeasure = res);

        this.translate.getAsync('Delete Item')
            .subscribe(t => this.deleteTitle = t);

        this.translate.getAsync('Are you sure you want to delete this item?')
            .subscribe(t => this.deleteConfirmation = t);
    }

    private createForms(): void {
        this.siteForm = this.fb.group({
            productionSize: [''],
            warehouseSize: [''],
            officeSize: [''],
            manufacturingEmployeeNo: [''],
            engineerEmployeeNo: [''],
            measureType: ['']
        });
    }
}
